/**
 * URL persistence for the session-list filter bar (#31348).
 *
 * Filter boxes live on the list component as signals; this module is the wire
 * format for carrying them through `/replay` ↔ `/replay/:sessionId` without
 * colliding with player-only query params (`appVersion`, `buildNumber`, `t`).
 */

export interface IRynReplaySessionFilterSnapshot {
  fromIso: string | null;
  toIso: string | null;
  userQuery: string;
  hasErrorsOnly: boolean;
  landingUrl: string;
  visitedUrl: string;
  appVersion: string;
  environment: string;
  browser: string;
}

export const RYN_REPLAY_SESSION_LIST_FILTERS_QUERY_PARAM = 'listFilters';

const asOptionalIso = (value: unknown): string | null =>
  typeof value === 'string' && value ? value : null;

const asText = (value: unknown): string =>
  typeof value === 'string' ? value : '';

export const isEmptyRynReplaySessionFilterSnapshot = (
  snapshot: IRynReplaySessionFilterSnapshot
): boolean =>
  !snapshot.fromIso &&
  !snapshot.toIso &&
  !snapshot.userQuery.trim() &&
  !snapshot.hasErrorsOnly &&
  !snapshot.landingUrl.trim() &&
  !snapshot.visitedUrl.trim() &&
  !snapshot.appVersion.trim() &&
  !snapshot.environment.trim() &&
  !snapshot.browser.trim();

/**
 * Serialize active filter boxes for the URL. Returns undefined when every box
 * is at its default so callers can omit the query param entirely.
 */
export const serializeRynReplaySessionFilterSnapshot = (
  snapshot: IRynReplaySessionFilterSnapshot
): string | undefined => {
  if (isEmptyRynReplaySessionFilterSnapshot(snapshot)) {
    return undefined;
  }
  return JSON.stringify(snapshot);
};

/**
 * Read a stored listFilters payload. Never throws; anything invalid is treated
 * as "no saved filters" so a hand-edited URL cannot blank the screen.
 */
export const parseRynReplaySessionFilterSnapshot = (
  raw: string | null | undefined
): IRynReplaySessionFilterSnapshot | null => {
  if (!raw) {
    return null;
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null) {
    return null;
  }

  const candidate = parsed as Record<string, unknown>;
  const snapshot: IRynReplaySessionFilterSnapshot = {
    fromIso: asOptionalIso(candidate['fromIso']),
    toIso: asOptionalIso(candidate['toIso']),
    userQuery: asText(candidate['userQuery']),
    hasErrorsOnly: candidate['hasErrorsOnly'] === true,
    landingUrl: asText(candidate['landingUrl']),
    visitedUrl: asText(candidate['visitedUrl']),
    appVersion: asText(candidate['appVersion']),
    environment: asText(candidate['environment']),
    browser: asText(candidate['browser']),
  };

  return isEmptyRynReplaySessionFilterSnapshot(snapshot) ? null : snapshot;
};

export const buildPlayerNavigationQueryParams = (input: {
  app?: string;
  listFilters?: IRynReplaySessionFilterSnapshot | null;
  playerAppVersion?: string | null;
  playerBuildNumber?: string | null;
}): Record<string, string> => {
  const params: Record<string, string> = {};

  const app = input.app?.trim();
  if (app) {
    params['app'] = app;
  }

  const serialized = input.listFilters
    ? serializeRynReplaySessionFilterSnapshot(input.listFilters)
    : undefined;
  if (serialized) {
    params[RYN_REPLAY_SESSION_LIST_FILTERS_QUERY_PARAM] = serialized;
  }

  const playerAppVersion = input.playerAppVersion?.trim();
  if (playerAppVersion) {
    params['appVersion'] = playerAppVersion;
  }
  const playerBuildNumber = input.playerBuildNumber?.trim();
  if (playerBuildNumber) {
    params['buildNumber'] = playerBuildNumber;
  }

  return params;
};

/** Strip player-only params so the grid URL restores just app + list filters. */
export const buildListBackQueryParams = (
  query: Record<string, string | null | undefined>
): Record<string, string> => {
  const params: Record<string, string> = {};

  const app = query['app']?.trim();
  if (app) {
    params['app'] = app;
  }

  const listFilters =
    query[RYN_REPLAY_SESSION_LIST_FILTERS_QUERY_PARAM]?.trim();
  if (listFilters) {
    params[RYN_REPLAY_SESSION_LIST_FILTERS_QUERY_PARAM] = listFilters;
  }

  return params;
};
